import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  RunnerTargetSelectComponent,
  type RunnerTargetInitial,
} from '../../../components/runner-target-select/runner-target-select.component';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import type { RunTarget } from '../../../services/execution-job.service';
import { TestRunScheduleService } from '../../../services/test-run-schedule.service';
import {
  TestRunSchedule,
  UpsertTestRunScheduleRequest,
  Weekday,
  WEEKDAYS,
  WORKDAYS,
} from '../../../models/test-run-schedule.model';
import { detectBrowserTimezone, timezoneOptions } from '../../../shared/time-zone.util';

export interface TestRunScheduleDialogData {
  projectId: string;
  runId: string;
  runKey: string;
  runName: string | null;
}

/** `schedule: null` heisst geloescht; schliesst der Dialog ohne Ergebnis, ist nichts geschehen. */
export interface TestRunScheduleDialogResult {
  schedule: TestRunSchedule | null;
}

type LoadState = 'loading' | 'loaded' | 'failed';

/**
 * Zeitplan eines Rechner-Laufs (NEX-1761): Wochentage, Uhrzeit, Zone, Ziel, aktiv.
 *
 * <p><b>Ohne Design-Entwurf.</b> Das Bundle hat keinen Zeitplan-Dialog
 * (page-planned.jsx:83-103 zeigt nur einen Platzhalter); gebaut im Stil des
 * Startdialogs `start-runner-round-dialog` (Team-Absprache 05.10.).
 */
@Component({
  selector: 'app-test-run-schedule-dialog',
  imports: [
    ConfirmDialogComponent,
    DatePipe,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    MatProgressSpinner,
    RouterLink,
    RunnerTargetSelectComponent,
    TranslatePipe,
  ],
  templateUrl: './test-run-schedule-dialog.component.html',
  styleUrl: './test-run-schedule-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestRunScheduleDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<TestRunScheduleDialogComponent, TestRunScheduleDialogResult>);
  private readonly scheduleService = inject(TestRunScheduleService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly data = inject<TestRunScheduleDialogData>(MAT_DIALOG_DATA);

  readonly weekdayOptions = WEEKDAYS;

  readonly loadState = signal<LoadState>('loading');
  /** Der gespeicherte Stand; `null`: Der Lauf hat noch keinen Zeitplan. */
  readonly original = signal<TestRunSchedule | null>(null);

  readonly weekdays = signal<ReadonlySet<Weekday>>(new Set());
  readonly time = signal('');
  readonly zone = signal(detectBrowserTimezone());
  readonly enabled = signal(true);
  readonly target = signal<RunTarget | null>(null);

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly confirmingDelete = signal(false);

  private readonly targetSelect = viewChild(RunnerTargetSelectComponent);

  readonly zones = computed(() => timezoneOptions(detectBrowserTimezone(), this.original()?.zone));

  /**
   * Erst gesetzt, wenn der Zeitplan geladen ist: Die Zielauswahl entsteht erst danach und waehlt beim
   * Eintreffen der Rechnerliste genau dieses Ziel. Gaebe es sie schon vorher, waere `initial` dann
   * noch leer, sie waehlte den eigenen Rechner, und Speichern wechselte das Ziel still.
   */
  readonly initialTarget = computed<RunnerTargetInitial | null>(() => {
    const original = this.original();
    return original ? { runnerId: original.runnerId, labels: original.requiredLabels } : null;
  });

  /**
   * Der gespeicherte Rechner fehlt in der Liste, oder der Server hat das Ziel entfernt. Mehrere
   * Etiketten (nur per API moeglich) bietet die Liste nicht an; das ist kein fehlender Rechner.
   */
  readonly targetMissing = computed(() => {
    const original = this.original();
    if (original === null || this.targetSelect()?.loadState() !== 'loaded' || this.target() !== null) {
      return false;
    }
    return original.runnerId !== null || original.requiredLabels.length === 0;
  });

  readonly orderedWeekdays = computed(() => WEEKDAYS.filter((day) => this.weekdays().has(day)));

  private readonly request = computed<UpsertTestRunScheduleRequest | null>(() => {
    const target = this.target();
    const time = this.time();
    const zone = this.zone();
    const weekdays = this.orderedWeekdays();
    if (target === null || time === '' || zone === '' || weekdays.length === 0) {
      return null;
    }
    return {
      weekdays,
      time,
      zone,
      runnerId: target.kind === 'runner' ? target.runnerId : null,
      requiredLabels: target.kind === 'labels' ? target.labels : [],
      enabled: this.enabled(),
    };
  });

  private readonly changed = computed(() => {
    const original = this.original();
    const request = this.request();
    if (original === null || request === null) {
      return true;
    }
    return (
      request.weekdays.join() !== WEEKDAYS.filter((day) => original.weekdays.includes(day)).join() ||
      request.time !== original.time.slice(0, 5) ||
      request.zone !== original.zone ||
      request.enabled !== original.enabled ||
      request.runnerId !== original.runnerId ||
      request.requiredLabels.join() !== original.requiredLabels.join()
    );
  });

  readonly canSave = computed(() => this.request() !== null && this.changed() && !this.saving());

  constructor() {
    this.load();
  }

  load(): void {
    this.loadState.set('loading');
    this.scheduleService
      .get(this.data.projectId, this.data.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (schedule) => {
          this.applyStored(schedule);
          this.loadState.set('loaded');
        },
        error: () => this.loadState.set('failed'),
      });
  }

  private applyStored(schedule: TestRunSchedule | null): void {
    this.original.set(schedule);
    if (schedule === null) {
      return;
    }
    this.weekdays.set(new Set(schedule.weekdays));
    this.time.set(schedule.time.slice(0, 5));
    this.zone.set(schedule.zone);
    this.enabled.set(schedule.enabled);
  }

  isSelected(day: Weekday): boolean {
    return this.weekdays().has(day);
  }

  toggleWeekday(day: Weekday): void {
    const next = new Set(this.weekdays());
    if (!next.delete(day)) {
      next.add(day);
    }
    this.weekdays.set(next);
  }

  /** Beide Kurzwahlen ersetzen die bisherige Wahl, statt sie zu ergaenzen. */
  preset(days: readonly Weekday[]): void {
    this.weekdays.set(new Set(days));
  }

  readonly workdays = WORKDAYS;

  onTimeInput(event: Event): void {
    this.time.set((event.target as HTMLInputElement).value);
  }

  onZoneChange(event: Event): void {
    this.zone.set((event.target as HTMLSelectElement).value);
  }

  onEnabledChange(event: Event): void {
    this.enabled.set((event.target as HTMLInputElement).checked);
  }

  save(): void {
    const request = this.request();
    if (!this.canSave() || request === null) {
      return;
    }
    this.busy(true);
    this.scheduleService
      .save(this.data.projectId, this.data.runId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (schedule) => this.dialogRef.close({ schedule }),
        error: (error: unknown) => {
          this.busy(false);
          this.saveError.set(this.errorMessage(error, 'testRunSchedule.dialog.saveError'));
        },
      });
  }

  askDelete(): void {
    this.confirmingDelete.set(true);
    this.dialogRef.disableClose = true;
  }

  cancelDelete(): void {
    this.confirmingDelete.set(false);
    this.dialogRef.disableClose = false;
  }

  confirmDelete(): void {
    this.busy(true);
    this.scheduleService
      .delete(this.data.projectId, this.data.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close({ schedule: null }),
        error: (error: unknown) => {
          this.busy(false);
          this.cancelDelete();
          this.saveError.set(this.errorMessage(error, 'testRunSchedule.dialog.deleteError'));
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  executionLink(executionId: string): string[] {
    return ['/projects', this.data.projectId, 'test-runs', this.data.runId, 'executions', executionId];
  }

  /** Waehrend der Server arbeitet, schliesst weder Escape noch ein Klick daneben den Dialog. */
  private busy(value: boolean): void {
    this.saving.set(value);
    this.saveError.set(null);
    this.dialogRef.disableClose = value;
  }

  /** 400 und 404 tragen den Grund als `{ error }` (ApiExceptionHandler), etwa einen getrennten Rechner. */
  private errorMessage(error: unknown, fallbackKey: string): string {
    if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404)) {
      const message = error.error?.error;
      if (typeof message === 'string' && message) {
        return message;
      }
    }
    return this.translate.instant(fallbackKey);
  }
}
